import { companyData } from '@/mocks/company';
import { defaultTemplateConfig, replaceYears } from '@/mocks/template-config';
import { getCategories } from '@/mocks/template-presets';
import type { ProposalMock } from '@/mocks/proposals';
import type { TemplateConfig } from '@/mocks/template-config';

interface PDFContentProps {
  proposal: ProposalMock;
  config?: TemplateConfig;
}

const UNIT_LABELS: Record<string, string> = {
  un: 'un',
  m: 'm',
  pc: 'pc',
  kit: 'kit',
  par: 'par',
  conj: 'conj',
  cx: 'cx',
  rol: 'rol',
  sv: 'sv',
};

export default function PDFContent({ proposal, config }: PDFContentProps) {
  const cfg = config || defaultTemplateConfig;
  const institutional = cfg.institutional;
  const visibleDifferentials = institutional.differentials.filter((d) => d.show);
  const activeSpecs = cfg.specialties.filter((s) => s.active).sort((a, b) => a.order - b.order);

  const subtotal = proposal.items.reduce((sum, item) => sum + item.total, 0);
  const serviceValue = (proposal as unknown as Record<string, unknown>).service_value as number || 0;
  const installationValue = (proposal as unknown as Record<string, unknown>).installation_value as number || 0;
  const servicesTotal = serviceValue + installationValue;
  const discount = (proposal as unknown as Record<string, unknown>).discount as number || 0;
  const totalValue = (proposal as unknown as Record<string, unknown>).total_value as number || proposal.totalValue;
  const validityDays = (proposal as unknown as Record<string, unknown>).validity_days as number | null;
  const expiresAt = (proposal as unknown as Record<string, unknown>).expires_at as string | null;
  const formattedExpires = expiresAt
    ? new Date(expiresAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : 'Sem expiração';
  const validityLabel = validityDays ? `${validityDays} dias` : 'Sem expiração';
  const proposalNumber = proposal.proposal_number || proposal.id;
  const createdAt = proposal.createdAt;
  const categoryName = getCategories().find(c => c.slug === proposal.category)?.name || proposal.category;

  const formatDate = (isoString: string | null | undefined) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const statusLabel =
    proposal.status === 'sent' ? 'Enviada' :
    proposal.status === 'approved' ? 'Aprovada' :
    proposal.status === 'rejected' ? 'Rejeitada' :
    proposal.status === 'draft' ? 'Rascunho' :
    proposal.status === 'viewed' ? 'Visualizada' :
    proposal.status;

  return (
    <div
      style={{
        width: '210mm',
        height: '297mm',
        backgroundColor: '#f5f7fa',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', Arial, sans-serif",
        boxSizing: 'border-box',
        padding: '0',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Cabeçalho azul escuro */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0a1628 0%, #0f1f35 100%)',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              border: '1.5px solid #c9a44a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span style={{ color: '#c9a44a', fontSize: '13px', fontWeight: 700, fontFamily: 'Georgia, serif' }}>
              E
            </span>
          </div>
          <div>
            <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: 700 }}>Eletrônica </span>
            <span style={{ color: '#c9a44a', fontSize: '13px', fontWeight: 700 }}>2000</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ color: '#c9a44a', fontSize: '10px', margin: 0, fontWeight: 600 }}>
            PROPOSTA COMERCIAL
          </p>
          <p style={{ color: '#9ca3af', fontSize: '9px', margin: 0 }}>
            Nº {proposalNumber}
          </p>
        </div>
      </div>

      {/* Corpo — área útil com margem de segurança para o rodapé */}
      <div
        style={{
          padding: '18px 24px',
          paddingBottom: '30mm',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {/* Título da proposta */}
        <h2
          style={{
            color: '#0a1628',
            fontSize: '17px',
            fontWeight: 700,
            margin: '0 0 3px 0',
          }}
        >
          {proposal.title || 'Proposta Comercial'}
        </h2>
        <p style={{ color: '#555555', fontSize: '12px', margin: '0 0 14px 0', fontStyle: 'italic' }}>
          {proposal.subtitle || 'Solução personalizada para sua necessidade'}
        </p>

        {/* Dados do cliente em linha */}
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '10px 14px',
            marginBottom: '14px',
            display: 'flex',
            gap: '18px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>Cliente</span>
            <span style={{ color: '#0a1628', fontSize: '12px', fontWeight: 600 }}>{proposal.clientName}</span>
          </div>
          {proposal.clientCompany && (
            <div>
              <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>Empresa</span>
              <span style={{ color: '#0a1628', fontSize: '12px', fontWeight: 600 }}>{proposal.clientCompany}</span>
            </div>
          )}
          <div>
            <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>Data</span>
            <span style={{ color: '#0a1628', fontSize: '12px', fontWeight: 600 }}>{formatDate(createdAt)}</span>
          </div>
          <div>
            <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>Validade</span>
            <span style={{ color: '#0a1628', fontSize: '12px', fontWeight: 600 }}>{proposal.validUntil}</span>
          </div>
          <div>
            <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>Status</span>
            <span style={{ color: '#c9a44a', fontSize: '12px', fontWeight: 600 }}>{statusLabel}</span>
          </div>
          {proposal.clientDocument && (
            <div>
              <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>CPF/CNPJ</span>
              <span style={{ color: '#0a1628', fontSize: '12px', fontWeight: 600 }}>{proposal.clientDocument}</span>
            </div>
          )}
          <div>
            <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>Categoria</span>
            <span style={{ color: '#0a1628', fontSize: '12px', fontWeight: 600 }}>{categoryName}</span>
          </div>
          {proposal.clientAddress && (
            <div>
              <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>Endereço</span>
              <span style={{ color: '#0a1628', fontSize: '12px', fontWeight: 600 }}>{proposal.clientAddress}</span>
            </div>
          )}
          <div>
            <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>Válida até</span>
            <span style={{ color: '#0a1628', fontSize: '12px', fontWeight: 600 }}>{formattedExpires}</span>
          </div>
          <div>
            <span style={{ color: '#888888', fontSize: '10px', textTransform: 'uppercase', display: 'block' }}>Validade</span>
            <span style={{ color: '#0a1628', fontSize: '12px', fontWeight: 600 }}>{validityLabel}</span>
          </div>
        </div>

        {/* Tabela de itens */}
        <h3
          style={{
            color: '#0a1628',
            fontSize: '13px',
            fontWeight: 700,
            margin: '0 0 8px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
          }}
        >
          Itens da Proposta
        </h3>

        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden',
            marginBottom: '14px',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#0a1628' }}>
                <th style={{ textAlign: 'left', color: '#c9a44a', fontSize: '10px', fontWeight: 600, padding: '6px 12px', textTransform: 'uppercase', width: '45%' }}>
                  Item
                </th>
                <th style={{ textAlign: 'center', color: '#c9a44a', fontSize: '10px', fontWeight: 600, padding: '6px 12px', textTransform: 'uppercase', width: '10%' }}>
                  Un
                </th>
                <th style={{ textAlign: 'center', color: '#c9a44a', fontSize: '10px', fontWeight: 600, padding: '6px 12px', textTransform: 'uppercase', width: '10%' }}>
                  Qtd
                </th>
                <th style={{ textAlign: 'right', color: '#c9a44a', fontSize: '10px', fontWeight: 600, padding: '6px 12px', textTransform: 'uppercase', width: '17.5%' }}>
                  Unitário
                </th>
                <th style={{ textAlign: 'right', color: '#c9a44a', fontSize: '10px', fontWeight: 600, padding: '6px 12px', textTransform: 'uppercase', width: '17.5%' }}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {proposal.items.map((item, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ color: '#333333', fontSize: '11px', padding: '6px 12px', lineHeight: 1.3 }}>
                    {item.description}
                  </td>
                  <td style={{ color: '#333333', fontSize: '11px', textAlign: 'center', padding: '6px 12px' }}>
                    {UNIT_LABELS[item.unitType] || item.unitType}
                  </td>
                  <td style={{ color: '#333333', fontSize: '11px', textAlign: 'center', padding: '6px 12px' }}>
                    {item.quantity}
                  </td>
                  <td style={{ color: '#333333', fontSize: '11px', textAlign: 'right', padding: '6px 12px' }}>
                    R$ {item.unitPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ color: '#333333', fontSize: '11px', textAlign: 'right', padding: '6px 12px', fontWeight: 600 }}>
                    R$ {item.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Resumo financeiro */}
        <div
          style={{
            display: 'flex',
            gap: '14px',
            marginBottom: '14px',
          }}
        >
          <div style={{ flex: 1 }}>
            <h3
              style={{
                color: '#0a1628',
                fontSize: '13px',
                fontWeight: 700,
                margin: '0 0 8px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}
            >
              Resumo Financeiro
            </h3>
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '12px 14px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span style={{ color: '#666666', fontSize: '11px' }}>Subtotal (itens)</span>
                <span style={{ color: '#333333', fontSize: '11px', fontWeight: 500 }}>
                  R$ {subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              {servicesTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span style={{ color: '#666666', fontSize: '11px' }}>Serviços</span>
                  <span style={{ color: '#333333', fontSize: '11px', fontWeight: 500 }}>
                    R$ {servicesTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span style={{ color: '#666666', fontSize: '11px' }}>Desconto</span>
                  <span style={{ color: '#e74c3c', fontSize: '11px', fontWeight: 500 }}>
                    - R$ {discount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              <div style={{ width: '100%', height: '1px', backgroundColor: '#e0e0e0', margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#0a1628', fontSize: '13px', fontWeight: 700 }}>TOTAL GERAL</span>
                <span style={{ color: '#0a1628', fontSize: '15px', fontWeight: 700 }}>
                  R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          <div style={{ width: '45mm', flexShrink: 0 }}>
            <h3 style={{ color: '#0a1628', fontSize: '13px', fontWeight: 700, margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              Informações
            </h3>
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div>
                <span style={{ color: '#888888', fontSize: '9px', textTransform: 'uppercase', display: 'block' }}>Validade</span>
                <span style={{ color: '#333333', fontSize: '11px', fontWeight: 600 }}>{validityLabel}</span>
              </div>
              <div>
                <span style={{ color: '#888888', fontSize: '9px', textTransform: 'uppercase', display: 'block' }}>Válida até</span>
                <span style={{ color: '#333333', fontSize: '11px', fontWeight: 600 }}>{formattedExpires}</span>
              </div>
              <div>
                <span style={{ color: '#888888', fontSize: '9px', textTransform: 'uppercase', display: 'block' }}>Status</span>
                <span style={{ color: '#c9a44a', fontSize: '11px', fontWeight: 600 }}>{statusLabel}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Condições + Observações lado a lado */}
        <div style={{ display: 'flex', gap: '14px', marginBottom: '14px' }}>
          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '12px 14px',
              flex: 1,
            }}
          >
            <h4 style={{ color: '#0a1628', fontSize: '11px', fontWeight: 700, margin: '0 0 6px 0', textTransform: 'uppercase' }}>
              Condições de Pagamento
            </h4>
            <p style={{ color: '#555555', fontSize: '10px', lineHeight: 1.5, margin: 0 }}>
              50% de entrada na aprovação da proposta e 50% na entrega/instalação.
              Formas de pagamento: PIX, transferência bancária ou cartão.
            </p>
          </div>

          <div
            style={{
              backgroundColor: '#fff8e7',
              border: '1px solid #c9a44a',
              borderRadius: '8px',
              padding: '12px 14px',
              flex: 1,
            }}
          >
            <h4 style={{ color: '#0a1628', fontSize: '11px', fontWeight: 700, margin: '0 0 6px 0', textTransform: 'uppercase' }}>
              Observações
            </h4>
            <p style={{ color: '#555555', fontSize: '10px', lineHeight: 1.5, margin: 0 }}>
              Proposta válida até a data indicada. Após aprovação, prazo de início será combinado conforme disponibilidade. Garantia de 12 meses em todos os equipamentos.
            </p>
          </div>
        </div>

        {/* ===== ASSINATURAS ===== */}
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '14px 18px',
            marginBottom: '14px',
          }}
        >
          <h4 style={{ color: '#0a1628', fontSize: '11px', fontWeight: 700, margin: '0 0 14px 0', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            Assinaturas
          </h4>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ width: '100%', height: '1px', backgroundColor: '#333333', marginBottom: '5px' }} />
              <p style={{ color: '#333333', fontSize: '11px', fontWeight: 600, margin: '0 0 2px 0' }}>
                {proposal.clientName}
              </p>
              {proposal.clientDocument && (
                <p style={{ color: '#666666', fontSize: '10px', margin: 0 }}>
                  CPF/CNPJ: {proposal.clientDocument}
                </p>
              )}
              <p style={{ color: '#888888', fontSize: '9px', margin: '2px 0 0 0' }}>Cliente</p>
            </div>

            <div style={{ width: '32px', flexShrink: 0 }} />

            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ width: '100%', height: '1px', backgroundColor: '#333333', marginBottom: '5px' }} />
              <p style={{ color: '#333333', fontSize: '11px', fontWeight: 600, margin: '0 0 2px 0' }}>
                Eletrônica 2000
              </p>
              <p style={{ color: '#888888', fontSize: '9px', margin: '2px 0 0 0' }}>Empresa / Responsável</p>
            </div>
          </div>
        </div>

        {/* Sobre nós */}
        {institutional.show && (
          <div style={{ marginBottom: '14px' }}>
            <h3
              style={{
                color: '#0a1628',
                fontSize: '13px',
                fontWeight: 700,
                margin: '0 0 8px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}
            >
              Sobre Nós
            </h3>
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '12px 14px',
              }}
            >
              <p style={{ color: '#444444', fontSize: '11px', lineHeight: 1.5, margin: '0 0 8px 0' }}>
                {replaceYears(institutional.text, institutional.foundedYear)}
              </p>
              {visibleDifferentials.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                  {visibleDifferentials.map((item, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, backgroundColor: '#c9a44a', borderRadius: '50%' }}>
                        <i className="ri-check-line" style={{ color: '#ffffff', fontSize: '10px' }} />
                      </div>
                      <span style={{ color: '#444444', fontSize: '10px', lineHeight: 1.3 }}>
                        {replaceYears(item.text, institutional.foundedYear)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Rodapé — fixo na parte inferior */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(135deg, #0a1628 0%, #0f1f35 100%)',
          padding: '10px 24px',
          height: '22mm',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: '1.5px solid #c9a44a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#c9a44a', fontSize: '11px', fontWeight: 700, fontFamily: 'Georgia, serif' }}>E</span>
            </div>
            <div>
              <span style={{ color: '#ffffff', fontSize: '11px', fontWeight: 700 }}>Eletrônica </span>
              <span style={{ color: '#c9a44a', fontSize: '11px', fontWeight: 700 }}>2000</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ color: '#9ca3af', fontSize: '9px' }}>{companyData.phone1}</span>
            <span style={{ color: '#9ca3af', fontSize: '9px' }}>{companyData.email}</span>
            <span style={{ color: '#9ca3af', fontSize: '9px' }}>{companyData.website}</span>
          </div>
        </div>
        {activeSpecs.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '6px', borderTop: '1px solid rgba(201,164,74,0.15)', paddingTop: '6px' }}>
            {activeSpecs.map((spec, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={`${spec.icon}`} style={{ color: '#c9a44a', fontSize: '9px' }} />
                </div>
                <span style={{ color: '#9ca3af', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  {spec.name}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}